"use client"
import { CSSProperties, useEffect, useState } from "react"
import { useSnapCarousel } from "react-snap-carousel"
import { twMerge } from "tailwind-merge"

interface CarouselRenderItemProps<T> {
  readonly item: T
  readonly isSnapPoint: boolean
}
interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly isSnapPoint: boolean
  readonly children?: React.ReactNode
}

interface CarouselProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  readonly items: T[]
  readonly renderItem: (props: CarouselRenderItemProps<T>) => React.ReactElement<CarouselItemProps>
  innerScrollProps?: React.HTMLAttributes<HTMLUListElement>
  prevNextButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  prevButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  nextButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  paginationButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
}

const styles = {
  root: {
    position: "relative",
  },
  scroll: {
    position: "relative",
    display: "flex",
    overflow: "auto",
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    msOverflowStyle: "none", // Internet Explorer 10+
  },
  itemSnapPoint: {
    scrollSnapAlign: "start",
  },
  controls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  nextPrevButtonDisabled: { color: "grey" },
  pagination: {
    display: "flex",
  },

  paginationButton: {
    margin: "10px",
  },
  paginationButtonActive: { color: "grey" },

  pageIndicator: {
    display: "flex",
    justifyContent: "center",
  },
} satisfies Record<string, CSSProperties>

export const Carousel = <T extends any>({
  items,
  renderItem,
  innerScrollProps,
  nextButtonProps,
  prevButtonProps,
  paginationButtonProps,
}: CarouselProps<T>) => {
  const { scrollRef, pages, goTo, prev, next, activePageIndex, hasPrevPage, hasNextPage, snapPointIndexes } =
    useSnapCarousel()

  const [isPaused, setIsPaused] = useState(false)
  const [direction, setDirection] = useState<"forward" | "backward">("forward")

  useEffect(() => {
    if (isPaused) return // Don't start interval if paused

    const interval = setInterval(() => {
      if (direction === "forward") {
        if (hasNextPage) {
          goTo(activePageIndex + 1)
        } else {
          // Reached the end, change direction
          setDirection("backward")
          if (hasPrevPage) goTo(activePageIndex - 1)
        }
      } else {
        if (hasPrevPage) {
          goTo(activePageIndex - 1)
        } else {
          // Reached the beginning, change direction
          setDirection("forward")
          if (hasNextPage) goTo(activePageIndex + 1)
        }
      }
    }, 3000) // Changed to 5 seconds as requested

    return () => clearInterval(interval)
  }, [activePageIndex, isPaused, direction, hasNextPage, hasPrevPage, goTo])
  // Added index as a deppendecy to make the interval will automatically reset when the user calls handleClick

  return (
    <div style={styles.root} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <ul
        style={styles.scroll}
        ref={scrollRef}
        {...innerScrollProps}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}>
        {items.map((item, i) =>
          renderItem({
            item,
            isSnapPoint: snapPointIndexes.has(i),
          }),
        )}
      </ul>
      <div style={styles.controls} aria-hidden>
        <button {...prevButtonProps} disabled={!hasPrevPage} onClick={() => prev()}>
          <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.707 12.293L4.414 7.99997H14V5.99997H4.414L8.707 1.70697L7.293 0.292969L0.585999 6.99997L7.293 13.707L8.707 12.293Z"
              fill="black"
            />
          </svg>
        </button>
        {pages.length > 1 &&
          pages.map((_, i) => (
            <button
              {...paginationButtonProps}
              key={i}
              id={activePageIndex === i ? "active" : ""}
              style={{
                ...styles.paginationButton,
                ...(activePageIndex === i ? styles.paginationButtonActive : {}),
              }}
              onClick={() => goTo(i)}>
              {i + 1}
            </button>
          ))}
        <button {...nextButtonProps} disabled={!hasNextPage} onClick={() => next()}>
          <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.293 12.293L6.707 13.707L13.414 6.99997L6.707 0.292969L5.293 1.70697L9.586 5.99997H0V7.99997H9.586L5.293 12.293Z"
              fill="black"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export const CarouselItem = ({ isSnapPoint, children, className }: CarouselItemProps) => (
  <li
    className={twMerge("flex-shrink-0", className)}
    style={{
      ...(isSnapPoint ? styles.itemSnapPoint : {}),
    }}>
    {children}
  </li>
)
